import { Suspense, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Badge } from '@givebutter/primitives/badge';
import { Button } from '@givebutter/primitives/button';
import { Dot } from '@givebutter/primitives/dot';
import { useToast } from '@givebutter/primitives/toast';
import { faFileExport, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteCampaign as deleteCampaignApi } from 'api/campaigns';
import { generateExports } from 'api/contacts/exports';
import Content from 'assets/js/components/Content';
import * as ExportDataDialog from 'assets/js/components/ExportDataDialog';
import { CampaignsTable } from 'assets/js/pages/campaigns/CampaignsTable';
import { CampaignsTableProvider } from 'assets/js/pages/campaigns/CampaignsTableProvider';
import { CopyCampaignCode } from 'assets/js/pages/campaigns/CopyCampaignCode';
import { PinCampaignDropdownItem } from 'assets/js/pages/campaigns/PinCampaignDropdownItem';
import Raised from 'assets/js/pages/campaigns/Raised';
import { ToggleCampaignStatusItem } from 'assets/js/pages/campaigns/ToggleCampaignStatusItem';
import { curriedTransformTableKeyToReadable } from 'assets/js/pages/campaigns/utils';
import * as CampaignModal from 'components/campaign-modal';
import * as CreateCampaignDialog from 'components/create-campaign-dialog';
import * as DropdownMenu from 'components/dropdown-menu';
import * as DuplicateCampaignModal from 'components/duplicate-campaign-modal';
import { FilterProvider } from 'components/filter/filters-next/FilterProvider';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { Permissions } from 'components/permissions';
import { CampaignPermission } from 'components/permissions/PermissionEnums';
import * as Table from 'components/table';
import { TableCoreResourceProvider } from 'components/table/TableCoreResourceContext';
import { TruncatedText } from 'components/truncated-text';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { isoDateFromServer } from 'helpers/dates';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Campaign } from 'types/domain';

const Campaigns = () => {
    const toast = useToast();
    const { newCampaignCreationOverlay = false } = useFlags();
    const { accountId } = useCurrentAccount();
    const { filters } = useFiltersByKey('campaigns');

    const { mutateAsync: exportCampaigns } = useMutation({
        mutationFn: () =>
            generateExports(accountId, {
                scope: 'all',
                object: 'account',
                report_type: 'csv',
                resource: 'campaigns',
                object_id: accountId,
                filters,
            }),
        onError: () => {
            toast.error('Unable to generate report');
        },
    });

    return (
        <FilterProvider cacheKey={'campaigns'} resource={'campaigns'}>
            <div className={'flex flex-col gap-y-4'}>
                <div className={'flex flex-wrap items-center justify-between gap-4'}>
                    <h1 className={'text-2xl font-medium leading-none'}>Campaigns</h1>
                    <div className={'flex flex-wrap items-center gap-1.5'}>
                        <ExportDataDialog.Root label="Campaigns" onSubmit={exportCampaigns}>
                            <ExportDataDialog.Trigger asChild={true}>
                                <Button variant="outline" color="neutral">
                                    <FontAwesomeIcon icon={faFileExport} />
                                    Export
                                </Button>
                            </ExportDataDialog.Trigger>
                        </ExportDataDialog.Root>
                        <Permissions allowed={[CampaignPermission.CREATE]}>
                            {newCampaignCreationOverlay ? (
                                <CreateCampaignDialog.Root
                                    triggerLocation={
                                        CreateCampaignDialog.TriggerLocation.CampaignsPage
                                    }
                                >
                                    <CreateCampaignDialog.Trigger asChild={true}>
                                        <Button variant="solid">
                                            <FontAwesomeIcon icon={faPlus} />
                                            New campaign
                                        </Button>
                                    </CreateCampaignDialog.Trigger>
                                </CreateCampaignDialog.Root>
                            ) : (
                                <CampaignModal.Root>
                                    <CampaignModal.Trigger asChild={true}>
                                        <Button variant="solid">
                                            <FontAwesomeIcon icon={faPlus} />
                                            New campaign
                                        </Button>
                                    </CampaignModal.Trigger>
                                </CampaignModal.Root>
                            )}
                        </Permissions>
                    </div>
                </div>
                <CampaignsTable>
                    {newCampaignCreationOverlay ? (
                        <CreateCampaignDialog.Root
                            triggerLocation={CreateCampaignDialog.TriggerLocation.CampaignsTable}
                        >
                            <CreateCampaignDialog.Trigger asChild={true}>
                                <Button variant="solid" className={'mt-5'} size={'sm'}>
                                    New Campaign
                                </Button>
                            </CreateCampaignDialog.Trigger>
                        </CreateCampaignDialog.Root>
                    ) : (
                        <CampaignModal.Root>
                            <CampaignModal.Trigger asChild={true}>
                                <Button variant="solid" className={'mt-5'} size={'sm'}>
                                    New Campaign
                                </Button>
                            </CampaignModal.Trigger>
                        </CampaignModal.Root>
                    )}
                </CampaignsTable>
            </div>
        </FilterProvider>
    );
};

const CampaignsWrapper = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const history = useHistory();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [campaignToDelete, setCampaignToDelete] = useState<Campaign | undefined>();
    const [showDuplicateCampaignModal, setShowDuplicateCampaignModal] = useState(false);
    const [campaignToDuplicate, setCampaignToDuplicate] = useState<Campaign | undefined>();

    const { mutate: deleteCampaign } = useMutation({
        mutationFn: () => deleteCampaignApi(campaignToDelete!.id),
        onSuccess: () => {
            toast.success('Campaign deleted!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.campaigns(accountId),
                type: 'all',
            });
        },
        onError: () => {
            toast.error('Unable to delete campaign');
        },
    });

    const onClickDuplicate = (campaign: Campaign) => {
        setCampaignToDuplicate(campaign);
        setShowDuplicateCampaignModal(true);
    };

    const onClickDelete = (campaign: Campaign) => {
        setCampaignToDelete(campaign);
        setShowDeleteModal(true);
    };

    return (
        <TableCoreResourceProvider
            resource={'campaigns'}
            initialState={{
                sortingState: [{ id: 'created_at', desc: true }],
                columnVisibilityState: {
                    url: false,
                },
            }}
            columns={columnHelper => [
                columnHelper.accessor('title', {
                    header: curriedTransformTableKeyToReadable,
                    enableHiding: false,
                    cell: info => {
                        const campaign = info.row.original;
                        const isExternal = campaign.type === 'external';

                        return (
                            <div className={'flex h-6 max-w-[230px] items-center gap-x-2'}>
                                {isExternal ? (
                                    <TruncatedText>
                                        <p className={'font-medium text-black'}>
                                            {info.getValue()}
                                        </p>
                                    </TruncatedText>
                                ) : (
                                    <TruncatedText>
                                        <a
                                            className={'text-black hover:underline'}
                                            target={'_blank'}
                                            href={campaign.url}
                                            rel="noreferrer"
                                        >
                                            {info.getValue()}
                                        </a>
                                    </TruncatedText>
                                )}
                                <Badge>{capitalize(campaign.type_friendly)}</Badge>
                                {!isExternal && <CopyCampaignCode campaign={campaign} />}
                            </div>
                        );
                    },
                    meta: {
                        ignoreLink: true,
                    },
                }),
                columnHelper.accessor('code', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => info.getValue(),
                }),
                columnHelper.accessor('donors', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info => info.getValue(),
                    meta: {
                        sortType: 'numeric',
                    },
                }),
                columnHelper.accessor('raised', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info => {
                        const campaign = info.row.original;
                        return <Raised item={campaign} />;
                    },
                    meta: {
                        sortType: 'numeric',
                    },
                }),
                columnHelper.accessor('url', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: () => <Table.CellLinkOrEmpty />,
                    meta: {
                        ignoreLink: true,
                    },
                }),
                columnHelper.accessor('active', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => {
                        let status = 'Inactive';
                        let indicatorColor = 'bg-red-500';
                        const campaign = info.row.original;
                        const goalMet = campaign.raised_percentage
                            ? campaign.raised_percentage >= 100
                            : false;

                        if (info.getValue()) {
                            status = 'Active';
                            indicatorColor = 'bg-green-500';
                        }

                        return (
                            <div className={'flex items-center gap-x-2'}>
                                {goalMet ? '🎉' : <Dot className={indicatorColor} />}
                                <p>{goalMet ? 'Completed' : status}</p>
                            </div>
                        );
                    },
                }),
                columnHelper.accessor('created_at', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info =>
                        isoDateFromServer(info.getValue()).toLocaleString(DateTime.DATE_FULL),
                    meta: {
                        sortType: 'date',
                    },
                }),
                columnHelper.display({
                    id: Table.TableConstants.Actions,
                    cell: info => {
                        const campaign = info.row.original;
                        const isExternal = campaign.type === 'external';
                        const disabledClass = isExternal
                            ? 'pointer-events-none opacity-60 hover:bg-transparent'
                            : '';

                        return (
                            <DropdownMenu.Root>
                                <Table.TableRowDropdownTrigger />
                                <DropdownMenu.Content sideOffset={5} align={'end'}>
                                    <DropdownMenu.Item
                                        className={disabledClass}
                                        onClick={e => {
                                            e.stopPropagation();
                                            history.push(
                                                `/accounts/${accountId}/campaigns/${campaign.id}`,
                                            );
                                        }}
                                        disabled={isExternal}
                                    >
                                        Edit
                                    </DropdownMenu.Item>
                                    <DropdownMenu.Item
                                        className={disabledClass}
                                        onClick={e => {
                                            e.stopPropagation();
                                            onClickDuplicate(campaign);
                                        }}
                                        disabled={isExternal}
                                    >
                                        Duplicate
                                    </DropdownMenu.Item>
                                    <PinCampaignDropdownItem campaign={campaign} />
                                    <Suspense
                                        fallback={
                                            <DropdownMenu.Item disabled={true}>
                                                Enable payments
                                            </DropdownMenu.Item>
                                        }
                                    >
                                        <ToggleCampaignStatusItem campaignId={campaign.id} />
                                    </Suspense>
                                    <DropdownMenu.Item
                                        onClick={e => {
                                            e.stopPropagation();
                                            onClickDelete(campaign);
                                        }}
                                    >
                                        Delete
                                    </DropdownMenu.Item>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        );
                    },
                    meta: {
                        ignoreLink: true,
                    },
                }),
            ]}
            options={{
                enableHiding: true,
                enableSorting: true,
                enablePinning: true,
                enableExpanding: true,
                meta: {
                    enableColumnOrder: true,
                },
            }}
        >
            <CampaignsTableProvider>
                <Content>
                    <Campaigns />
                    {campaignToDuplicate && (
                        <DuplicateCampaignModal.Root
                            autoCloseAfterSuccess={false}
                            open={showDuplicateCampaignModal}
                            onDuplicateSuccess={() => setShowDuplicateCampaignModal(false)}
                            onOpenChange={open => {
                                setShowDuplicateCampaignModal(open);
                                if (!open) {
                                    setCampaignToDuplicate(undefined);
                                }
                            }}
                            campaign={campaignToDuplicate}
                        />
                    )}
                    <AlertDialog.Root
                        type={'error'}
                        open={showDeleteModal}
                        onOpenChange={setShowDeleteModal}
                    >
                        <AlertDialog.Portal>
                            <AlertDialog.Overlay />
                            <AlertDialog.Content>
                                <AlertDialog.Header>
                                    <AlertDialog.Icon />
                                    <AlertDialog.Close />
                                </AlertDialog.Header>
                                <AlertDialog.Body>
                                    <AlertDialog.Title>Delete Campaign</AlertDialog.Title>
                                    <AlertDialog.Description>
                                        Are you sure? You can't undo this action afterwards.
                                    </AlertDialog.Description>
                                </AlertDialog.Body>
                                <AlertDialog.Footer>
                                    <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                    <AlertDialog.Action onClick={() => deleteCampaign()}>
                                        Delete
                                    </AlertDialog.Action>
                                </AlertDialog.Footer>
                            </AlertDialog.Content>
                        </AlertDialog.Portal>
                    </AlertDialog.Root>
                </Content>
            </CampaignsTableProvider>
        </TableCoreResourceProvider>
    );
};

export default CampaignsWrapper;
